<template>
  <div class="expired-invite" data-testid="expired-invite-link">
    <div class="expired-invite__card">
      <img
        class="expired-invite__logo"
        src="@components/assets/login/CompanyLogo.png"
        alt="exIQtive"
      />
      <div
        ref="statusRegion"
        class="expired-invite__status"
        tabindex="-1"
        aria-live="polite"
        aria-atomic="true"
        data-testid="expired-invite-status"
      >
        <h1 class="expired-invite__title">{{ content.title }}</h1>
        <p class="expired-invite__text">{{ content.text }}</p>
      </div>
      <Button
        fullwidth="hover"
        :label="buttonLabel"
        :loading="isSending ? 'left' : null"
        :disabled="isDisabled"
        data-testid="expired-invite-request"
        @onClick="onRequest"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from "vue";
import Button from "@components/buttons/Button.vue";

const MESSAGES = {
  en: {
    expiredTitle: "Invite link expired",
    expiredText:
      "This invitation link is no longer valid. Request a new link and we will send it to the invited email address if the invitation is still open.",
    requestLabel: "Request a new link",
    sendingLabel: "Sending…",
    sentTitle: "Check your email",
    sentText:
      "If the invitation is still open, a new link is on its way to the invited email address. Check your spam folder.",
    rateLimitedTitle: "Too many requests",
    rateLimitedText: "Too many requests. Try again later.",
  },
};

const resolveMessages = (language) => {
  const code = String(language || "en")
    .toLowerCase()
    .split("-")[0];
  return MESSAGES[code] || MESSAGES.en;
};

const props = defineProps({
  status: {
    type: String,
    default: "idle",
    validator: (value) => ["idle", "sending", "sent", "rate_limited"].includes(value),
  },
});

const emit = defineEmits(["request-new-link"]);

const messages = resolveMessages(typeof navigator === "undefined" ? "en" : navigator.language);
const statusRegion = ref(null);

const isSending = computed(() => props.status === "sending");
const isDisabled = computed(() => props.status === "sending" || props.status === "sent");
const buttonLabel = computed(() =>
  isSending.value ? messages.sendingLabel : messages.requestLabel
);

const content = computed(() => {
  if (props.status === "sent") {
    return { title: messages.sentTitle, text: messages.sentText };
  }
  if (props.status === "rate_limited") {
    return { title: messages.rateLimitedTitle, text: messages.rateLimitedText };
  }
  return { title: messages.expiredTitle, text: messages.expiredText };
});

watch(
  () => props.status,
  async (status) => {
    if (status !== "sent") return;
    await nextTick();
    statusRegion.value?.focus();
  }
);

const onRequest = () => {
  if (isDisabled.value) return;
  emit("request-new-link");
};
</script>

<style scoped>
.expired-invite {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
  background-color: #f9fafb;
}
.expired-invite__card {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 440px;
  padding: 40px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background-color: #ffffff;
}
.expired-invite__logo {
  align-self: flex-start;
  max-width: 100%;
  height: auto;
}
.expired-invite__status {
  border-radius: 4px;
}
.expired-invite__status:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 4px;
}
.expired-invite__title {
  margin: 0 0 8px;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  color: #101828;
}
.expired-invite__text {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #344054;
}
</style>
